import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu, X } from 'lucide-react';
import useAuth from '../contexts/useAuth';
import { ADA_TAGLINE, CHAT_PAGE_PATH } from '@/utils/constants';
import { SideBar } from './SiderBar';
import { ConversationMessage } from '../utils/dynamodb';
import { clearUserProfile } from '@/services/userProfileService';
import logo from '@/assets/logo.webp';

export const Layout: React.FC<{
  children: React.ReactNode;
  sidebar?: boolean;
  fullPage?: boolean;
  conversationTitle?: string | null;
  onLoadConversation?:
    | ((
        messages: ConversationMessage[],
        sessionId: string,
        title?: string,
      ) => void)
    | null;
}> = ({
  children,
  sidebar,
  fullPage = false,
  conversationTitle,
  onLoadConversation,
}) => {
  const { user, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleSignOut = () => {
    clearUserProfile();
    logout();
    navigate('/get-started');
  };

  return (
    <div
      className={`w-full flex flex-col bg-allianz-light overflow-x-hidden ${
        fullPage
          ? 'min-h-screen' // document scroll for pages like GetStarted/Login
          : 'h-screen overflow-hidden' // fixed viewport for chat
      }`}
    >
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 flex-shrink-0">
        <div className="px-3 sm:px-6 lg:px-8 flex justify-between items-center h-14">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            {/* Hamburger for mobile sidebar */}
            {sidebar && (
              <button
                className="md:hidden p-1.5 rounded-md text-gray-600 hover:bg-gray-100 flex-shrink-0"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open sidebar"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}
            <img
              src={logo}
              alt="Allianz"
              className="h-8 sm:h-10 w-auto cursor-pointer flex-shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
              onClick={() => {
                if (window.location.pathname !== CHAT_PAGE_PATH) {
                  window.location.href = CHAT_PAGE_PATH;
                } else {
                  window.location.reload();
                }
              }}
            />
            <div className="h-6 border-l border-gray-300 hidden sm:block flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <span className="font-bold text-gray-900 leading-tight block truncate">
                {conversationTitle ? (
                  <span className="text-sm sm:text-base">
                    {conversationTitle}
                  </span>
                ) : (
                  <>
                    <span className="sm:hidden text-xl">Ada</span>
                    <span className="hidden sm:inline text-sm sm:text-base">
                      {ADA_TAGLINE}
                    </span>
                  </>
                )}
              </span>
              {conversationTitle && (
                <div className="text-xs text-gray-500 mt-0.5 truncate">
                  <span className="sm:hidden">Ada</span>
                  <span className="hidden sm:inline">{ADA_TAGLINE}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            {(userProfile || user) && (
              <>
                <span className="hidden sm:inline text-sm text-gray-600 truncate max-w-[160px]">
                  {userProfile?.email || user?.username}
                </span>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm font-medium text-white bg-gray-500 hover:bg-red-600 rounded transition-colors"
                >
                  <LogOut className="h-4 w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Drawer */}
      {sidebar && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 md:hidden ${
              drawerOpen
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer panel */}
          <div
            className={`fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 shadow-xl flex flex-col transition-transform duration-200 md:hidden ${
              drawerOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-800 text-sm">Menu</span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
                aria-label="Close sidebar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <SideBar onLoadConversation={onLoadConversation} />
            </div>
          </div>
        </>
      )}

      {/* Main Content with optional Sidebar */}
      <div className={`flex w-full ${fullPage ? 'h-auto' : 'flex-1 min-h-0'}`}>
        {sidebar && (
          <aside className="hidden md:block w-64 flex-shrink-0 bg-white border-r border-gray-200 p-4 overflow-y-auto shadow-md">
            <SideBar onLoadConversation={onLoadConversation} />
          </aside>
        )}
        <main
          className={`max-w-full ${fullPage ? 'w-full' : 'flex-1 min-h-0 overflow-hidden'}`}
        >
          {children}
        </main>
      </div>

      {/* Common Footer */}
      <footer className="bg-allianz-light text-center text-sm text-gray-500 py-4 border-t border-gray-200 flex-shrink-0 relative z-10 w-full">
        <div className="flex items-center justify-center gap-4">
          <span>© {new Date().getFullYear()} Allianz Australia</span>
          <span>•</span>
          <a
            href="https://www.allianzretireplus.com.au/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-allianz-primary transition-colors"
          >
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
};
